import { ProofImageService } from './proof-image.service';
import { inject, Injectable } from '@angular/core';
import {
	CartItem,
	isCartItem,
	isOrderProduct,
	ItemSource,
} from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import { filterNil } from '@app/shared/utils/util';
import {
	type RemoteData,
	Failure,
	Initialized,
	isFailure,
	Pending,
	Success,
} from '@granodigital/grano-remote-data';
import { Observable, of } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';

/** Configuration object containing details needed for proof image operations */
export interface ProofImageDetails {
	/** UUID of the item (cart item or order product) */
	itemId: string;
	/** Permanent editor state ID of the printfile */
	stateId: string;
	/** Source context - required for regeneration, omit for order products */
	itemSource?: ItemSource;
}

/** Recovery status for image error recovery attempts */
export type RecoveryStatus = 'regenerating' | 'success' | 'failed';

/** Service for handling all product image processing with proof image fallback chain for CartItems, OrderProducts, and Products. */
@Injectable({ providedIn: 'root' })
export class ProductImageService {
	private readonly proofImageService = inject(ProofImageService);
	private readonly PROOF_IMAGE_PATTERN =
		/(?<prefix>.*\/api\/rendering\/GetProofImage\/)(?<userId>[^/]+)\/(?<stateId>[^/]+)(?<suffix>\/.*)/;
	private readonly PRINTFILE_PATTERN = /editor-(?<stateId>.+?)\.pdf/;

	/** Type guard to check if item supports proof images */
	private hasProofImageSupport(
		itemOrProduct: CartItem | api.OrderProductDto | api.ProductDto,
	): itemOrProduct is CartItem | api.OrderProductDto {
		return isCartItem(itemOrProduct) || isOrderProduct(itemOrProduct);
	}

	/**
	 * Create ProofImageDetails object for the given item
	 * @param item Cart item or order product
	 * @param itemSource Source context (required for cart items with regeneration, omit for order products)
	 * @returns ProofImageDetails object or undefined if data is incomplete
	 */
	createProofImageDetails(
		item: CartItem | api.OrderProductDto,
		itemSource?: ItemSource,
	): ProofImageDetails | undefined {
		const itemId = isCartItem(item) ? item.id : item.uuid;
		const stateId = this.getProductStateId(item);

		return itemId && stateId ? { itemId, stateId, itemSource } : undefined;
	}

	/**
	 * Collect all available images with automatic fallback chain:
	 * 1. Try proof images first if they exist in productFlowData (CartItem/OrderProduct only)
	 * 2. Fallback to static product images
	 * 3. Component will handle final fallback to generic image on load error
	 * For orders: Replace temporary state IDs with permanent ones since regeneration is not possible
	 * For standalone products: Only static images are available
	 */
	collectAllImages(
		itemOrProduct: CartItem | api.OrderProductDto | api.ProductDto,
	): api.ProductImageDto[] {
		const proofImages = this.hasProofImageSupport(itemOrProduct)
			? this.collectProofImages(itemOrProduct)
			: [];
		return proofImages.length > 0 ? proofImages : this.collectStaticProductImages(itemOrProduct);
	}

	/**
	 * Collect proof images from cart item or order product
	 * Returns normalized proof images or empty array if none available
	 */
	private collectProofImages(item: CartItem | api.OrderProductDto): api.ProductImageDto[] {
		const thumbnail = isCartItem(item)
			? item.productFlowData?.proofImageThumbnail
			: item.product_flow_data?.proof_image_thumbnail;
		const scaledImages = isCartItem(item)
			? item.productFlowData?.scaledProofImages
			: item.product_flow_data?.scaled_proof_images;

		// Return empty array if proof images are not available
		if (!thumbnail || !scaledImages || !Array.isArray(scaledImages) || scaledImages.length === 0) {
			return [];
		}

		// For cart items, use URLs as-is since regeneration handles expired ones
		if (isCartItem(item)) {
			return this.normalizeProofImages(scaledImages, thumbnail);
		}

		// For order products, replace temporary state IDs with permanent ones
		const permanentStateId = this.getProductStateId(item);
		return permanentStateId
			? this.normalizeProofImages(
					scaledImages.map((url) => this.replaceStateId(url, permanentStateId)),
					this.replaceStateId(thumbnail, permanentStateId),
				)
			: this.normalizeProofImages(scaledImages, thumbnail);
	}

	/**
	 * Common normalization logic for proof images - maps scaled images to ProductImageDto format
	 * The first image gets both fitIn_2000x2000 and fitIn_80x80 (thumbnail)
	 * TODO (ED-2581): This won't be needed when the proof images are uploaded to S3
	 */
	private normalizeProofImages(scaledImages: string[], thumbnail: string): api.ProductImageDto[] {
		return scaledImages.map((scaledImage: string, index: number) => ({
			fitIn_2000x2000: scaledImage,
			...(index === 0 && thumbnail && { fitIn_80x80: thumbnail }),
		}));
	}

	/**
	 * Extract static product images from CartItem, OrderProductDto, or ProductDto
	 * Only handles product.images - does not include proof images
	 * @param itemOrProduct CartItem, OrderProductDto, or ProductDto to extract image data from
	 * @returns Array of ProductImageDto objects from the product's static images
	 */
	collectStaticProductImages(
		itemOrProduct: CartItem | api.OrderProductDto | api.ProductDto,
	): api.ProductImageDto[] {
		const product =
			isCartItem(itemOrProduct) || isOrderProduct(itemOrProduct) ? itemOrProduct.product : itemOrProduct;
		if (!product?.images) {
			return [];
		}

		const { master, additional = [] } = product.images;
		return [master, ...additional].filter(filterNil);
	}

	/** Replace image URL temporary state ID with permanent state ID */
	private replaceStateId(imageUrl: string, stateId: string): string {
		return imageUrl.replace(this.PROOF_IMAGE_PATTERN, `$<prefix>$<userId>/${stateId}$<suffix>`);
	}

	/** Find product state ID in printfile key */
	getProductStateId(item: CartItem | api.OrderProductDto): string | undefined {
		if (isCartItem(item)) {
			return item.options?.printfile?.editor_state_id;
		}

		const printfileKey = item.options?.printfile_key;
		return printfileKey ? this.PRINTFILE_PATTERN.exec(printfileKey)?.groups?.stateId : undefined;
	}

	/** Check if regeneration is possible for the given parameters */
	private canRegenerate(proofImageDetails?: ProofImageDetails, userId?: string): boolean {
		return !!(
			proofImageDetails?.itemSource &&
			proofImageDetails?.itemId &&
			proofImageDetails?.stateId &&
			userId
		);
	}

	/**
	 * Get initial state for a new image URL
	 * @param imageUrl The image URL to get initial state for
	 * @returns Pending if URL exists, Initialized if no URL
	 */
	getInitialImageState(imageUrl: string | undefined): RemoteData<Error, string> {
		return imageUrl ? new Pending() : new Initialized();
	}

	/**
	 * Handle successful image load with validation
	 * Only marks as successful if we're displaying the actual input URL (not fallback)
	 * @param inputImageUrl The original input URL
	 * @param currentDisplayUrl The currently displayed URL
	 * @param currentState Current image state
	 * @returns Updated state if load should be marked successful, or current state if not
	 */
	handleImageLoad(
		inputImageUrl: string | undefined,
		currentDisplayUrl: string,
		currentState: RemoteData<Error, string>,
	): RemoteData<Error, string> {
		// Only mark as successful if we're displaying the actual input URL (not fallback)
		if (inputImageUrl && currentDisplayUrl === inputImageUrl) {
			return new Success(inputImageUrl);
		}
		// If we're showing fallback image, don't change state - the original URL is still broken
		return currentState;
	}

	/**
	 * Get the display URL based on image state
	 * @param inputUrl The original image URL
	 * @param state Current RemoteData state
	 * @param fallbackUrl Fallback URL to use when image fails
	 * @returns URL to display (either input URL or fallback)
	 */
	getDisplayUrl(
		inputUrl: string | undefined,
		state: RemoteData<Error, string>,
		fallbackUrl: string,
	): string {
		// Show fallback if no URL or in failure state
		return !inputUrl || isFailure(state) ? fallbackUrl : inputUrl;
	}

	/**
	 * Handle complete image error recovery with RemoteData state management
	 * @param failedImageUrl The image URL that failed to load
	 * @param proofImageDetails Details needed for proof image regeneration (optional for static images)
	 * @param currentState Current RemoteData state to check if recovery should be attempted
	 * @returns Observable that emits RemoteData states: Pending → Success/Failure
	 */
	handleImageError(
		failedImageUrl: string,
		proofImageDetails?: ProofImageDetails,
		currentState?: RemoteData<Error, string>,
	): Observable<RemoteData<Error, string>> {
		// Don't attempt recovery if already failed to avoid infinite loops
		if (currentState && isFailure(currentState)) {
			return of(currentState);
		}

		// No URL to recover - return failure
		if (!failedImageUrl) {
			return of(new Failure(new Error('No image URL to regenerate')));
		}

		const userId = this.PROOF_IMAGE_PATTERN.exec(failedImageUrl)?.groups?.userId;

		if (!this.canRegenerate(proofImageDetails, userId) || !proofImageDetails || !userId) {
			return of(new Failure(new Error('Image regeneration failed')));
		}

		const regenerationService = this.getRegenerationService(proofImageDetails.itemSource);
		if (!regenerationService) {
			return of(new Failure(new Error('Image regeneration failed')));
		}

		return regenerationService(proofImageDetails.itemId, proofImageDetails.stateId, userId).pipe(
			map(() => new Success(failedImageUrl)),
			catchError(() => of(new Failure(new Error('Image regeneration failed')))),
			startWith(new Pending()),
		);
	}

	/** Get the appropriate regeneration service based on item source or fallback to route detection */
	private getRegenerationService(itemSource?: ItemSource) {
		switch (itemSource) {
			case ItemSource.MyProducts:
				return this.proofImageService.regenerateMyProductProofImages.bind(this.proofImageService);
			case ItemSource.ShoppingCart:
				return this.proofImageService.regenerateCartItemProofImages.bind(this.proofImageService);
			default:
				return;
		}
	}
}
